import { useState } from 'react';
import VideoFeed from './VideoFeed';

const VideoGrid = ({
  localStream,
  remoteStreams = {},
  isLocalScreenSharing = false,
  isCameraEnabled = false,
  // LiveKit-specific props - separate for camera and screen
  localCameraTrack = null,
  localScreenTrack = null,
  remoteLiveKitTracks = {},
  localCameraMetadata = null,
  localScreenMetadata = null,
  remoteTrackMetadata = {},

  remoteCameraStreams = {}, // ✅ CORRECT
  remoteScreenStreams = {},

  // Separate streams for simultaneous camera and screen share
  localCameraStream = null,
  localScreenShareStream = null,
}) => {
  const [hoveredPipId, setHoveredPipId] = useState(null)

  // Helper to check if a stream has active video tracks
  const hasActiveVideoTracks = (stream) => {
    if (!stream) return false
    const videoTracks = stream.getVideoTracks()
    return (
      videoTracks.length > 0 &&
      videoTracks.some((track) => track.enabled && track.readyState === "live")
    )
  }

  const isLocalScreenShareActive = hasActiveVideoTracks(localScreenShareStream)

  const remoteStreamEntries = Object.entries(remoteStreams)
  const totalStreams = remoteStreamEntries.length + (localStream ? 1 : 0)

  // Check for screen sharing participants - verify actual streams
  const screenSharingParticipants = remoteStreamEntries
    .filter(([id, data]) => {
      const screenStream = remoteScreenStreams?.[id]
      // If we have a dedicated screen stream, check if it has active tracks
      if (screenStream) {
        return hasActiveVideoTracks(screenStream)
      }
      // Otherwise, fall back to checking data flags and data.stream
      return (
        (data.isScreenShare || data.isScreenSharing) &&
        hasActiveVideoTracks(data.stream)
      )
    })
    .map(([id]) => id)

  const isAnyRemoteScreenSharing = screenSharingParticipants.length > 0
  const firstScreenShareId = screenSharingParticipants[0]

  // ============================================================================
  // SCREEN SHARING LAYOUTS (when someone is sharing screen)
  // ============================================================================

  if (
    (isLocalScreenShareActive || isAnyRemoteScreenSharing) &&
    totalStreams > 1
  ) {
    // ---------------------------------------------------------------------------
    // TWO PARTICIPANTS WITH SCREEN SHARING: 70/30 Split Layout
    // Screen share on left (70%), camera feeds stacked on right (30%)
    // ---------------------------------------------------------------------------
    if (totalStreams === 2) {
      const remoteParticipantId = firstScreenShareId
      const remoteScreenStream = remoteScreenStreams?.[remoteParticipantId]

      return (
        <div className="h-full flex gap-2">
          {/* Left side: Screen share (70% width) */}
          <div className="flex-[7] h-full">
            {isLocalScreenShareActive ? (
              <VideoFeed
                stream={localScreenShareStream || localStream}
                participantName="Your screen"
                isSelf={true}
                livekitTrack={localScreenTrack}
                trackMetadata={localScreenMetadata}
                isScreenSharing={true}
                isCameraOn={false}
              />
            ) : (
              <VideoFeed
                stream={
                  remoteScreenStream || remoteStreams[firstScreenShareId].stream
                }
                participantName={firstScreenShareId}
                isSelf={false}
                livekitTrack={remoteLiveKitTracks[firstScreenShareId]}
                trackMetadata={remoteTrackMetadata[firstScreenShareId]}
                isScreenSharing={true}
                isCameraOn={false}
              />
            )}
          </div>

          {/* Right side: Camera feeds stacked (30% width) */}
          <div className="flex-[3] h-full flex flex-col gap-2">
            {/* Top: Remote participant's camera */}
            {remoteStreamEntries.length > 0 &&
              (() => {
                const remoteId = remoteStreamEntries[0][0]
                const remoteCameraStream =
                  remoteCameraStreams?.[remoteId] || new MediaStream()
                const isRemoteCameraActive =
                  hasActiveVideoTracks(remoteCameraStream)

                return (
                  <div className="flex-1">
                    <VideoFeed
                      key={remoteId}
                      stream={remoteCameraStream}
                      participantName={remoteId}
                      isSelf={false}
                      livekitTrack={remoteLiveKitTracks[remoteId]}
                      trackMetadata={remoteTrackMetadata[remoteId]}
                      isScreenSharing={false}
                      isCameraOn={isRemoteCameraActive}
                    />
                  </div>
                )
              })()}

            {/* Bottom: Self camera feed */}
            <div className="flex-1">
              <VideoFeed
                stream={localCameraStream || localStream}
                participantName="You"
                isSelf={true}
                livekitTrack={localCameraTrack}
                trackMetadata={localCameraMetadata}
                isScreenSharing={false}
                isCameraOn={isCameraEnabled}
              />
            </div>
          </div>
        </div>
      )
    }

    if (totalStreams >= 3) {
      const remoteParticipantId = firstScreenShareId
      const remoteScreenStream = remoteScreenStreams?.[remoteParticipantId]

      // Determine if we're showing local or remote screen as main (prefer remote)
      const isMainScreenLocal =
        isLocalScreenShareActive && !isAnyRemoteScreenSharing

      // Collect all camera feeds to show on the right (including those without active video)
      const allCameraFeeds = []

      // Add local camera - ALWAYS show even if camera is off
      allCameraFeeds.push({
        type: "local",
        stream: localCameraStream || new MediaStream(),
        participantName: "You",
        isSelf: true,
        livekitTrack: localCameraTrack,
        trackMetadata: localCameraMetadata,
        isScreenSharing: false,
        isCameraOn: isCameraEnabled,
      })

      // Add all remote camera feeds - ALWAYS show even if camera is off
      remoteStreamEntries.forEach(([participantId, data]) => {
        const remoteCameraStream = remoteCameraStreams?.[participantId]
        const isRemoteCameraActive = hasActiveVideoTracks(remoteCameraStream)

        allCameraFeeds.push({
          type: "remote",
          id: participantId,
          stream: remoteCameraStream || new MediaStream(),
          participantName: { participantId },
          isSelf: false,
          livekitTrack: remoteLiveKitTracks[participantId],
          trackMetadata: remoteTrackMetadata[participantId],
          isScreenSharing: false,
          isCameraOn: isRemoteCameraActive,
        })
      })

      // Add local screen share if it's active but NOT the main screen on the left
      if (isLocalScreenShareActive && !isMainScreenLocal) {
        allCameraFeeds.push({
          type: "local-screen",
          id: "local-screen",
          stream: localScreenShareStream,
          participantName: "Your screen",
          isSelf: true,
          livekitTrack: localScreenTrack,
          trackMetadata: localScreenMetadata,
          isScreenSharing: false,
          isCameraOn: false,
        })
      }

      // Add any additional remote screen shares (not the main one shown on left)
      remoteStreamEntries.forEach(([participantId, data]) => {
        if (participantId !== firstScreenShareId) {
          const screenStream = remoteScreenStreams?.[participantId]
          if (screenStream && hasActiveVideoTracks(screenStream)) {
            allCameraFeeds.push({
              type: "remote-screen",
              id: `${participantId}-screen`,
              stream: screenStream,
              participantName: { participantId },
              isSelf: false,
              livekitTrack: remoteLiveKitTracks[participantId],
              trackMetadata: remoteTrackMetadata[participantId],
              isScreenSharing: true,
              isCameraOn: false,
            })
          }
        }
      })

      return (
        <div className="h-full flex gap-2">
          {/* Left side: Main screen share (70% width) - Prefer remote screen */}
          <div className="flex-[7] h-full">
            {isAnyRemoteScreenSharing ? (
              <VideoFeed
                stream={
                  remoteScreenStream ||
                  remoteStreams[firstScreenShareId]?.stream ||
                  new MediaStream()
                }
                participantName={firstScreenShareId}
                isSelf={false}
                livekitTrack={remoteLiveKitTracks[firstScreenShareId]}
                trackMetadata={remoteTrackMetadata[firstScreenShareId]}
                isScreenSharing={true}
                isCameraOn={false}
              />
            ) : isLocalScreenShareActive ? (
              <VideoFeed
                stream={localScreenShareStream || localStream}
                participantName="Your screen"
                isSelf={true}
                livekitTrack={localScreenTrack}
                trackMetadata={localScreenMetadata}
                isScreenSharing={true}
                isCameraOn={false}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-800 rounded-lg">
                <p className="text-gray-300">No screen share available</p>
              </div>
            )}
          </div>

          {/* Right side: All camera feeds + additional screen shares stacked (30% width) */}
          <div className="flex-[3] h-full flex flex-col gap-2">
            {allCameraFeeds.map((feed, index) => (
              <div
                key={feed.type === "local" ? "local-camera" : feed.id}
                className="flex-1"
              >
                <VideoFeed
                  stream={feed.stream}
                  participantName={feed.participantName}
                  isSelf={feed.isSelf}
                  livekitTrack={feed.livekitTrack}
                  trackMetadata={feed.trackMetadata}
                  isScreenSharing={feed.isScreenSharing}
                  isCameraOn={feed.isCameraOn}
                />
              </div>
            ))}
          </div>
        </div>
      )
    }
  }

  // ============================================================================
  // REGULAR LAYOUTS (no screen sharing)
  // ============================================================================

  // ---------------------------------------------------------------------------
  // SINGLE PARTICIPANT: Full screen view
  // ---------------------------------------------------------------------------
  if (totalStreams === 1) {
    if (localStream && remoteStreamEntries.length === 0) {
      return (
        <div className="h-full">
          <VideoFeed
            stream={localStream}
            participantName="You"
            isSelf={true}
            livekitTrack={localCameraTrack}
            trackMetadata={localCameraMetadata}
            isScreenSharing={isLocalScreenShareActive}
            isCameraOn={isCameraEnabled}
          />
        </div>
      )
    }

    if (remoteStreamEntries.length === 1 && !localStream) {
      const [participantId, data] = remoteStreamEntries[0]
      return (
        <div className="h-full">
          <VideoFeed
            stream={data.stream}
            participantName={participantId}
            isSelf={false}
            livekitTrack={remoteLiveKitTracks[participantId]}
            trackMetadata={remoteTrackMetadata[participantId]}
            isScreenSharing={
              data.isScreenShare || data.isScreenSharing || false
            }
            isCameraOn={data.isCameraOn || false}
          />
        </div>
      )
    }
  }

  // ---------------------------------------------------------------------------
  // TWO PARTICIPANTS (no screen sharing): Remote large + Local PiP
  // ---------------------------------------------------------------------------
  if (totalStreams === 2) {
    return (
      <div className="relative h-full">
        {/* Remote participant takes main view */}
        {remoteStreamEntries.map(([participantId, data]) => (
          <div key={participantId} className="w-full h-full">
            <VideoFeed
              stream={remoteCameraStreams?.[participantId] || data.stream}
              participantName={participantId}
              isSelf={false}
              livekitTrack={remoteLiveKitTracks[participantId]}
              trackMetadata={remoteTrackMetadata[participantId]}
              isScreenSharing={
                data.isScreenShare || data.isScreenSharing || false
              }
              isCameraOn={hasActiveVideoTracks(
                remoteCameraStreams?.[participantId] || data.stream
              )}
            />
          </div>
        ))}

        {/* Local stream as PiP */}
        {localStream && (
          <div
            className={`absolute bottom-3 left-3 ${
              hoveredPipId === "local" ? "w-64 h-48" : "w-64 h-40"
            } 
              z-10 shadow-lg rounded-lg overflow-hidden
              transition-all duration-300 ease-in-out`}
            onMouseEnter={() => setHoveredPipId("local")}
            onMouseLeave={() => setHoveredPipId(null)}
          >
            <VideoFeed
              stream={localCameraStream || localStream}
              participantName="You"
              isSelf={true}
              isPiP={true}
              livekitTrack={localCameraTrack}
              trackMetadata={localCameraMetadata}
              isScreenSharing={isLocalScreenShareActive}
              isCameraOn={isCameraEnabled}
            />
          </div>
        )}
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // THREE PARTICIPANTS: 2 on top row, 1 centered on bottom row
  // ---------------------------------------------------------------------------
  if (totalStreams === 3) {
    const allParticipants = [
      ...(localStream
        ? [
            {
              type: "local",
              stream: localStream,
              livekitTrack: localCameraTrack,
              trackMetadata: localCameraMetadata,
              isScreenSharing: isLocalScreenShareActive,
              isCameraOn: isCameraEnabled,
            },
          ]
        : []),
      ...remoteStreamEntries.map(([id, data]) => ({
        type: "remote",
        id,
        stream: data.stream,
        livekitTrack: remoteLiveKitTracks[id],
        trackMetadata: remoteTrackMetadata[id],
        isScreenSharing: data.isScreenShare || data.isScreenSharing || false,
        isCameraOn: data.isCameraOn || false,
        ...data,
      })),
    ]

    return (
      <div className="h-full flex flex-col gap-2">
        {/* First row: 2 participants */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          {allParticipants.slice(0, 2).map((participant, index) => (
            <div
              key={participant.type === "local" ? "local" : participant.id}
              className="w-full h-full"
            >
              {participant.type === "local" ? (
                <VideoFeed
                  stream={participant.stream}
                  participantName="You"
                  isSelf={true}
                  livekitTrack={participant.livekitTrack}
                  trackMetadata={participant.trackMetadata}
                  isScreenSharing={participant.isScreenSharing}
                  isCameraOn={participant.isCameraOn}
                />
              ) : (
                <VideoFeed
                  stream={participant.stream}
                  participantName={participant.id}
                  isSelf={false}
                  livekitTrack={participant.livekitTrack}
                  trackMetadata={participant.trackMetadata}
                  isScreenSharing={participant.isScreenSharing}
                  isCameraOn={participant.isCameraOn}
                />
              )}
            </div>
          ))}
        </div>

        {/* Second row: 1 participant centered */}
        <div className="flex-1 flex justify-center">
          <div className="w-1/2 h-full">
            {(() => {
              const participant = allParticipants[2]
              return participant.type === "local" ? (
                <VideoFeed
                  stream={participant.stream}
                  participantName="You"
                  isSelf={true}
                  livekitTrack={participant.livekitTrack}
                  trackMetadata={participant.trackMetadata}
                  isScreenSharing={participant.isScreenSharing}
                  isCameraOn={participant.isCameraOn}
                />
              ) : (
                <VideoFeed
                  stream={participant.stream}
                  participantName={participant.id}
                  isSelf={false}
                  livekitTrack={participant.livekitTrack}
                  trackMetadata={participant.trackMetadata}
                  isScreenSharing={participant.isScreenSharing}
                  isCameraOn={participant.isCameraOn}
                />
              )
            })()}
          </div>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // FOUR OR MORE PARTICIPANTS: Responsive grid layout
  // ---------------------------------------------------------------------------
  const getGridClasses = () => {
    if (totalStreams <= 4) {
      return "grid grid-cols-2 grid-rows-2 gap-2 h-full"
    } else if (totalStreams <= 6) {
      return "grid grid-cols-2 lg:grid-cols-3 gap-2 h-full"
    } else {
      return "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 h-full"
    }
  }

  return (
    <div className={getGridClasses()}>
      {/* Local stream */}
      {localStream && (
        <div className="w-full h-full">
          <VideoFeed
            stream={localStream}
            participantName="You"
            isSelf={true}
            livekitTrack={localCameraTrack}
            trackMetadata={localCameraMetadata}
            isScreenSharing={isLocalScreenShareActive}
            isCameraOn={isCameraEnabled}
          />
        </div>
      )}

      {/* Remote streams */}
      {remoteStreamEntries.map(([participantId, data]) => (
        <div key={participantId} className="w-full h-full">
          <VideoFeed
            stream={data.stream}
            participantName={participantId}
            isSelf={false}
            livekitTrack={remoteLiveKitTracks[participantId]}
            trackMetadata={remoteTrackMetadata[participantId]}
            isScreenSharing={
              data.isScreenShare || data.isScreenSharing || false
            }
            isCameraOn={data.isCameraOn || false}
          />
        </div>
      ))}
    </div>
  )
}

export default VideoGrid;